import { useExcursionFiltersTotalCount } from '../../composables/excursion/useExcursionFiltersTotalCount';
import {
  getExcursionInitialFiltersState,
  setExcursionInitialFilterState,
} from '../../model/excursion/ExcursionInitial.ts';
import type { TExcursionFiltersPageState, TExcursionFiltersState } from '../../types/excursion/state';
import type { LocationQuery, Router } from 'vue-router';
import { excursionBuildQueryFilterParams } from '@/entities/filter/store/utils/excursion/ExcursionBuildQueryFilterParams.ts';
import {
  excursionCheckAppliedFilters,
  excursionCheckOnlyDateAppliedFilters,
} from '@/entities/filter/store/utils/excursion/ExcursionCheckAppliedFilters.ts';
import { getPathWithoutTag } from '@/entities/filter/store/utils/removeTagFromPath.ts';
import { useClearPriceFilterOnCommittedDisplayCurrencyChange } from '@/entities/filter/lib/clearPriceFilterOnDisplayCurrencyChange';
import type { ExtendedPartTagType } from '@/entities/rubrics';
import { applyRubricSelectionState } from '@/entities/filter/store/utils/applyRubricSelection.ts';

export const useExcursionFiltersStore = defineStore('excursion-filters-state', () => {
  const router = ref<Router | null>(null);

  function initRouter(r: Router) {
    router.value = r;
  }
  /**
   * Состояние фильтров от последнего полного поиска
   */
  const searchState: TExcursionFiltersState = reactive(getExcursionInitialFiltersState());
  /**
   * Состояние текущих выбранных фильтров (до прожатия кнопки "показать")
   */
  const state: TExcursionFiltersState = reactive(getExcursionInitialFiltersState());

  /**
   * Параметры фильтров, устанавливаемые на уровне страницы
   */
  const pageParams: TExcursionFiltersPageState = reactive({});

  /**
   * TODO переделать! Пока что флаг следит за тем, что первые Э уже прогрузились
   */
  const isFirstExperiencesLoaded = ref(false);

  /**
   * Получение сведений о количестве предложений при выборе фильтров (до применения)
   */
  const { totalCount, isLoading } = useExcursionFiltersTotalCount(state, pageParams, {
    useDraftDisplayCurrency: true,
  });

  /**
   * Получение сведений о количестве предложений после применения фильтров
   */
  const { totalCount: searchTotalCount, isLoading: isSearchLoading } = useExcursionFiltersTotalCount(
    searchState,
    pageParams,
  );

  /**
   * Выполнение поиска по текущим установленным фильтрам
   */
  function search() {
    const stateClone = structuredClone(toRaw(state));
    Object.assign(searchState, stateClone);
    setFilterQueryParams();
  }

  function setFilterQueryParams() {
    if (!router.value) {
      return;
    }

    let currentPath = searchState.selected_rubric?.url
      ? searchState.selected_rubric.url
      : router.value?.currentRoute?.path;

    if (!state.tag__slugs) {
      currentPath = getPathWithoutTag(
        router.value?.currentRoute?.path || '',
        (router.value?.currentRoute?.params?.tag as string) || '',
      );
    }

    const searchParams = new URLSearchParams(window.location.search);
    const currentQuery = Object.fromEntries(searchParams.entries());

    const params = excursionBuildQueryFilterParams(searchState);

    const nextQuery = {
      ...currentQuery,
      ...params,
    };

    for (const [k, v] of Object.entries(params)) {
      if (v === undefined) {
        delete nextQuery[k];
      }
    }

    router.value?.push({
      path: currentPath || '',
      query: nextQuery,
    });
  }

  function resetModalFilters() {
    const keysToReset = [
      'type',
      'tourFormat',
      'price',
      'movementTypes',
      'duration',
      'startTimeInterval',
      'durationInterval',
      'childrenAllowed',
      'sorting',
      'date',
    ] satisfies (keyof TExcursionFiltersState)[];

    keysToReset.forEach((key) => {
      if (key !== 'date') {
        reset(key);
      }
    });
  }

  /**
   * Сброс всех изменений фильтров до состояния последнего поиска
   */
  function reset(key: keyof TExcursionFiltersState | 'modal' | 'persons') {
    const initial = getExcursionInitialFiltersState();

    if (key === 'modal') {
      resetModalFilters();

      return;
    }

    if (key === 'persons') {
      state.date.persons = 1;

      return;
    }

    // Особый случай: формат проведения
    if (key === 'type' || key === 'tourFormat') {
      state.type = initial.type;
      state.tourFormat = initial.tourFormat;

      return;
    }

    // остальные ключи как раньше
    Object.assign(state, { [key]: initial[key] });
  }

  function setInitialStore(queryParams: LocationQuery, tag: string) {
    // Сохраняем selected_rubric перед обновлением, чтобы не потерять его при парсинге query параметров
    const savedSelectedRubric = state.selected_rubric || searchState.selected_rubric;

    const defaultParams = setExcursionInitialFilterState(queryParams, tag);
    Object.assign(state, defaultParams);

    // Восстанавливаем selected_rubric, если он был установлен и tag совпадает
    if (savedSelectedRubric && (state.tag__slugs === savedSelectedRubric.value || tag === savedSelectedRubric.value)) {
      state.selected_rubric = savedSelectedRubric;
    }

    Object.assign(searchState, structuredClone(toRaw(state)));
  }

  function applyPreviousParams() {
    Object.assign(state, structuredClone(toRaw(searchState)));
  }

  function applyRubricSelection(rubric: ExtendedPartTagType) {
    applyRubricSelectionState(state, rubric);
    search();
  }

  function syncSelectedRubric(rubric: ExtendedPartTagType) {
    if (searchState.tag__slugs !== rubric.value) {
      return;
    }

    state.selected_rubric = rubric;
    searchState.selected_rubric = rubric;
  }

  const isFiltersApplied = computed(() => excursionCheckAppliedFilters(searchState));
  const isFiltersOnlyDateApplied = computed(() => excursionCheckOnlyDateAppliedFilters(searchState));

  useClearPriceFilterOnCommittedDisplayCurrencyChange({
    state,
    searchState,
    search,
  });

  return {
    state,
    searchState,
    isLoading,
    totalCount,
    pageParams,
    isFirstExperiencesLoaded,
    setInitialStore,
    search,
    applyRubricSelection,
    syncSelectedRubric,
    reset,
    applyPreviousParams,
    initRouter,
    isFiltersApplied,
    isFiltersOnlyDateApplied,
    searchTotalCount,
    isSearchLoading,
  };
});
